import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { getRequestStatusLabel } from "../../utils/requestHelpers";
import {
  filterCurrentlyOnLeaveRequests,
  filterTeamLeaveRequests,
  filterTeamOvertimeRequests,
  formatTeamRequestDetails,
  getEmployeeLeaveRemaining,
  matchesTeamMemberSearch,
  matchesTeamRequestSearch,
  matchesTeamRequestStatus,
  overtimeSettlementLabel,
  requestStatusUiKey,
} from "../../utils/teamRequestsHelpers";
import { LeaveRemainingValue } from "./TeamLeaveRemaining";
import "./TeamLeaveOvertimeView.css";

const STATUS_FILTERS = [
  { id: "all", label: "الكل" },
  { id: "pending", label: "قيد المراجعة" },
  { id: "approved", label: "معتمد" },
  { id: "rejected", label: "مرفوض" },
];

function StatusBadge({ status }) {
  return (
    <span className={`ess-status-badge ess-status-${requestStatusUiKey(status)}`}>
      {getRequestStatusLabel(status)}
    </span>
  );
}

function RequestCard({ request, showSettlement }) {
  return (
    <article className="team-lo-card">
      <div className="team-lo-card-top">
        <div>
          <h3 className="team-lo-card-name">{request.employeeName || "—"}</h3>
          <p className="team-lo-card-code">{request.employeeCode || ""}</p>
        </div>
        <StatusBadge status={request.status} />
      </div>
      <p className="team-lo-card-type">{request.type}</p>
      <p className="team-lo-card-details">{formatTeamRequestDetails(request)}</p>
      {request.workplaceLabel ? (
        <p className="team-lo-card-place">{request.workplaceLabel}</p>
      ) : null}
      {showSettlement && (request.requestedSettlement || request.settlement) ? (
        <p className="team-lo-card-settle">
          {request.requestedSettlement
            ? `رغبة: ${overtimeSettlementLabel(request.requestedSettlement)}`
            : ""}
          {request.requestedSettlement && request.settlement ? " · " : ""}
          {request.settlement
            ? `قرار HR: ${overtimeSettlementLabel(request.settlement)}`
            : ""}
        </p>
      ) : null}
      {request.reason ? (
        <p className="team-lo-card-reason">{request.reason}</p>
      ) : null}
    </article>
  );
}

function LeaveTable({ rows }) {
  return (
    <table className="team-lo-table">
      <thead>
        <tr>
          <th>الموظف</th>
          <th>النوع</th>
          <th>التفاصيل</th>
          <th>مكان العمل</th>
          <th>السبب</th>
          <th>الحالة</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((request) => (
          <tr key={request.id}>
            <td>
              <div className="team-lo-emp">{request.employeeName || "—"}</div>
              <span className="team-lo-code">{request.employeeCode || ""}</span>
            </td>
            <td>{request.type}</td>
            <td>{formatTeamRequestDetails(request)}</td>
            <td>{request.workplaceLabel || "—"}</td>
            <td>{request.reason || "—"}</td>
            <td>
              <StatusBadge status={request.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function memberStatusLabel(status) {
  if (status === "on-leave") return "في إجازة";
  if (status === "active") return "نشط";
  if (status === "terminated") return "منتهي الخدمة";
  return "غير نشط";
}

function BalancesTable({ rows }) {
  return (
    <table className="team-lo-table">
      <thead>
        <tr>
          <th>الموظف</th>
          <th>المسمى</th>
          <th>اعتيادية</th>
          <th>عارضة</th>
          <th>الحالة</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((member) => {
          const leave = getEmployeeLeaveRemaining(member);
          return (
            <tr key={member.id}>
              <td>
                <div className="team-lo-emp">{member.name || "—"}</div>
                <span className="team-lo-code">{member.code || ""}</span>
              </td>
              <td>{member.jobTitle || "—"}</td>
              <td>
                <LeaveRemainingValue
                  remaining={leave.annualRemaining}
                  entitlement={leave.annualEntitlement}
                />
              </td>
              <td>
                <LeaveRemainingValue
                  remaining={leave.casualRemaining}
                  entitlement={leave.casualEntitlement}
                />
              </td>
              <td>{memberStatusLabel(member.status)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function BalanceCard({ member }) {
  const leave = getEmployeeLeaveRemaining(member);
  return (
    <article className="team-lo-card">
      <div className="team-lo-card-top">
        <div>
          <h3 className="team-lo-card-name">{member.name || "—"}</h3>
          <p className="team-lo-card-code">{member.code || ""}</p>
        </div>
        <span className="team-lo-card-type">{memberStatusLabel(member.status)}</span>
      </div>
      {member.jobTitle ? (
        <p className="team-lo-card-details">{member.jobTitle}</p>
      ) : null}
      <div className="team-lo-balance-grid">
        <div>
          <span>اعتيادية</span>
          <LeaveRemainingValue
            remaining={leave.annualRemaining}
            entitlement={leave.annualEntitlement}
          />
        </div>
        <div>
          <span>عارضة</span>
          <LeaveRemainingValue
            remaining={leave.casualRemaining}
            entitlement={leave.casualEntitlement}
          />
        </div>
      </div>
    </article>
  );
}

function OvertimeTable({ rows }) {
  return (
    <table className="team-lo-table">
      <thead>
        <tr>
          <th>الموظف</th>
          <th>الساعات</th>
          <th>الفترة</th>
          <th>التسوية</th>
          <th>السبب</th>
          <th>الحالة</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((request) => (
          <tr key={request.id}>
            <td>
              <div className="team-lo-emp">{request.employeeName || "—"}</div>
              <span className="team-lo-code">{request.employeeCode || ""}</span>
            </td>
            <td>{Number(request.hours || 0).toFixed(2)} ساعة</td>
            <td>
              {request.endDate && request.endDate !== request.startDate
                ? `${request.startDate} → ${request.endDate}`
                : request.startDate || "—"}
            </td>
            <td>
              {request.requestedSettlement
                ? `رغبة: ${overtimeSettlementLabel(request.requestedSettlement)}`
                : "—"}
              {request.settlement
                ? `${request.requestedSettlement ? " · " : ""}قرار HR: ${overtimeSettlementLabel(request.settlement)}`
                : ""}
            </td>
            <td>{request.reason || "—"}</td>
            <td>
              <StatusBadge status={request.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function TeamLeaveOvertimeView() {
  const { user } = useAuth();
  const { pendingRequests, teamMembers } = useData();
  const [tab, setTab] = useState("leave");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const leaveRows = useMemo(
    () => filterTeamLeaveRequests(pendingRequests, user),
    [pendingRequests, user],
  );
  const overtimeRows = useMemo(
    () => filterTeamOvertimeRequests(pendingRequests, user),
    [pendingRequests, user],
  );
  const currentlyOnLeave = useMemo(
    () => filterCurrentlyOnLeaveRequests(leaveRows),
    [leaveRows],
  );
  const balanceRows = useMemo(
    () =>
      [...(teamMembers || [])]
        .filter((member) => matchesTeamMemberSearch(member, searchTerm))
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "ar"),
        ),
    [teamMembers, searchTerm],
  );

  const visibleRows = useMemo(() => {
    const source = tab === "overtime" ? overtimeRows : leaveRows;
    return source.filter(
      (request) =>
        matchesTeamRequestSearch(request, searchTerm) &&
        matchesTeamRequestStatus(request, statusFilter),
    );
  }, [tab, overtimeRows, leaveRows, searchTerm, statusFilter]);

  return (
    <div className="team-lo" dir="rtl">
      <div className="team-lo-header">
        <div>
          <h1 className="team-lo-title">إجازات وإضافي الفريق</h1>
          <p className="team-lo-subtitle">
            طلبات الإجازة والإضافي وأرصدة الإجازات المتبقية لموظفين الفريق
          </p>
        </div>
      </div>

      {currentlyOnLeave.length > 0 && (
        <section className="team-lo-now">
          <h2 className="team-lo-now-title">
            حالياً في إجازة ({currentlyOnLeave.length})
          </h2>
          <ul className="team-lo-now-list">
            {currentlyOnLeave.map((request) => (
              <li key={request.id} className="team-lo-now-item">
                <strong>{request.employeeName || "—"}</strong>
                <span>
                  {request.type}
                  {request.endDate ? ` حتى ${request.endDate}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="team-lo-tabs">
        <button
          type="button"
          className={`team-lo-tab${tab === "leave" ? " is-active" : ""}`}
          onClick={() => setTab("leave")}
        >
          الإجازات والأذونات ({leaveRows.length})
        </button>
        <button
          type="button"
          className={`team-lo-tab${tab === "overtime" ? " is-active" : ""}`}
          onClick={() => setTab("overtime")}
        >
          الإضافي ({overtimeRows.length})
        </button>
        <button
          type="button"
          className={`team-lo-tab${tab === "balances" ? " is-active" : ""}`}
          onClick={() => setTab("balances")}
        >
          أرصدة الإجازات ({teamMembers?.length || 0})
        </button>
      </div>

      <div className="team-lo-toolbar">
        <input
          type="search"
          className="team-lo-search"
          placeholder="بحث بالاسم أو الكود..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {tab !== "balances" ? (
          <div className="team-lo-filters">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`team-lo-filter${
                  statusFilter === filter.id ? " is-active" : ""
                }`}
                onClick={() => setStatusFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {tab === "balances" ? (
        balanceRows.length === 0 ? (
          <p className="team-lo-empty">لا يوجد موظفون في الفريق</p>
        ) : (
          <>
            <div className="team-lo-table-wrap">
              <BalancesTable rows={balanceRows} />
            </div>
            <div className="team-lo-cards">
              {balanceRows.map((member) => (
                <BalanceCard key={member.id} member={member} />
              ))}
            </div>
          </>
        )
      ) : visibleRows.length === 0 ? (
        <p className="team-lo-empty">
          {tab === "overtime"
            ? "لا توجد طلبات إضافي للفريق"
            : "لا توجد طلبات إجازة أو إذن للفريق"}
        </p>
      ) : (
        <>
          <div className="team-lo-table-wrap">
            {tab === "overtime" ? (
              <OvertimeTable rows={visibleRows} />
            ) : (
              <LeaveTable rows={visibleRows} />
            )}
          </div>
          <div className="team-lo-cards">
            {visibleRows.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                showSettlement={tab === "overtime"}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
